/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React from 'react'
import Icon from '@mdsol/lego/lib/Icon'

interface TableHeadCellProps {
  id?: string
  className?: string
  style?: React.CSSProperties
  children: React.ReactNode
  sort?: string
  sortDir?: 'asc' | 'desc'
  isCurrent?: boolean
  onSort?: (dir: 'asc' | 'desc') => void
}

export const TableHeadCell: React.FC<TableHeadCellProps> = ({
  id,
  className,
  style,
  children,
  sort,
  sortDir,
  isCurrent,
  onSort
}) => {
  const defaultStyle: React.CSSProperties = { fontSize: '14px', ...style }

  if (!sort || !onSort) {
    return (
      <th id={id} className={className} style={defaultStyle}>
        {children}
      </th>
    )
  }

  const handleSort = (e: React.MouseEvent) => {
    e.preventDefault()
    if (!isCurrent) {
      onSort(sortDir || 'desc')
    } else {
      onSort(sortDir === 'asc' ? 'desc' : 'asc')
    }
  }

  const sortIcon = sortDir === 'asc' ? 'carat-up' : 'carat-down'
  const sortLinkClass = `sort${isCurrent ? ' ed' : ''}`

  return (
    <th id={id} className={className} style={defaultStyle}>
      <a
        href="#"
        data-sort={sort}
        data-sort-dir={isCurrent ? sortDir : 'none'}
        className={sortLinkClass}
        onClick={handleSort}
      >
        {children}
        <span> </span>
        {isCurrent && <Icon className="icon-xsmall" name={sortIcon} />}
      </a>
    </th>
  )
}

interface TableHeadRowProps {
  children: React.ReactNode
  sort?: string
  sortDir?: 'asc' | 'desc'
  onSort?: (sortName: string, dir: 'asc' | 'desc') => void
  className?: string
}

export const TableHeadRow: React.FC<TableHeadRowProps> = ({ children, sort, sortDir, onSort, className }) => {
  const childrenArray = React.Children.toArray(children)

  return (
    <tr className={className}>
      {childrenArray.map((child) => {
        if (!React.isValidElement(child)) {
          return child
        }

        const childProps = child.props as any

        // If the child has a sort prop, it's sortable - pass sorting props
        if (childProps.sort && onSort) {
          const isCurrent = childProps.sort && sort === childProps.sort

          return React.cloneElement(child, {
            key: childProps.sort || childProps.id,
            isCurrent,
            sortDir: sortDir,
            onSort: (dir: 'asc' | 'desc') => onSort(childProps.sort, dir)
          } as any)
        }

        // Non-sortable column
        return React.cloneElement(child, {
          key: childProps.id || childProps.sort || Math.random()
        } as any)
      })}
    </tr>
  )
}

interface TableRowProps {
  children: React.ReactNode
  [key: string]: any
}

export const TableRow: React.FC<TableRowProps> = ({ children, ...props }) => <tr {...props}>{children}</tr>
